import * as enabledApi from '@shared/api/enabledPlayApi';
import INotificationModel from '@shared/models/users/NotificationModel';
import React from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface NotificationState {
    // State
    errors: string[];
    loading: boolean;
    notifications: INotificationModel[];
    unreadNotifications: INotificationModel[];

    // Actions
    generateNotification: (notification: INotificationModel) => Promise<void>;
    getUserNotificationsAll: () => Promise<void>;
    getUserNotificationsUnread: () => Promise<void>;
    initializeFromRemote: () => Promise<void>;
    markNotificationRead: (notificationId: string) => Promise<void>;
    setErrors: (errors: string[]) => void;
    setLoading: (loading: boolean) => void;
    setNotifications: (notifications: INotificationModel[]) => void;
    setUnreadNotifications: (unreadNotifications: INotificationModel[]) => void;
}

export const useNotificationStore = create<NotificationState>()(
    immer((set, get) => {
        const setNotifications = (notifications: INotificationModel[]) =>
            set((state) => {
                state.notifications = notifications;
            });

        const setUnreadNotifications = (unreadNotifications: INotificationModel[]) =>
            set((state) => {
                state.unreadNotifications = unreadNotifications;
            });

        const setErrors = (errors: string[]) =>
            set((state) => {
                state.errors = errors;
            });

        const setLoading = (loading: boolean) =>
            set((state) => {
                state.loading = loading;
            });

        const initializeFromRemote = async () => {
            await get().getUserNotificationsAll();
        };

        const getUserNotificationsUnread = async () => {
            set((state) => {
                state.loading = true;
            });
            try {
                const allNotes = await enabledApi.getUserNotificationsUnread();
                if (allNotes?.resultType === 'Ok' && allNotes.data) {
                    set((state) => {
                        state.unreadNotifications = allNotes.data!.filter((n) => !n.isRead);
                    });
                } else {
                    set((state) => {
                        state.errors = ['Failed to load notifications'];
                    });
                    console.warn('Error initializing notifications:', allNotes);
                }
            } catch (e) {
                set((state) => {
                    state.errors = [e.message];
                });
                console.error('Error initializing notifications:', e);
            }
            set((state) => {
                state.loading = false;
            });
        };

        const getUserNotificationsAll = async () => {
            set((state) => {
                state.loading = true;
            });
            try {
                const allNotes = await enabledApi.getUserNotificationsAll();
                if (allNotes?.resultType === 'Ok' && allNotes.data) {
                    set((state) => {
                        state.notifications = allNotes.data!;
                        state.unreadNotifications = allNotes.data!.filter((n) => !n.isRead);
                    });
                } else {
                    set((state) => {
                        state.errors = ['Failed to load notifications'];
                    });
                    console.warn('Error initializing notifications:', allNotes);
                }
            } catch (e) {
                set((state) => {
                    state.errors = [e.message];
                });
                console.error('Error initializing notifications:', e);
            }
            set((state) => {
                state.loading = false;
            });
        };

        const markNotificationRead = async (notificationId: string) => {
            set((state) => {
                state.loading = true;
            });
            try {
                const markResult = await enabledApi.markNotificationRead(notificationId);
                if (markResult?.resultType === 'Ok' && markResult.data) {
                    set((state) => {
                        const noteIndex = state.notifications.findIndex((n) => n.id === notificationId);
                        if (noteIndex > -1) {
                            state.notifications[noteIndex] = markResult.data!;
                            state.unreadNotifications = state.notifications.filter((n) => !n.isRead);
                        }
                    });
                } else {
                    set((state) => {
                        state.errors = ['Failed to mark notification as read'];
                    });
                }
            } catch (e) {
                set((state) => {
                    state.errors = [e.message];
                });
            }
            set((state) => {
                state.loading = false;
            });
        };

        const generateNotification = async (notification: INotificationModel) => {
            set((state) => {
                state.loading = true;
            });
            try {
                const generateResult = await enabledApi.generateNotification(notification);
                if (generateResult?.resultType === 'Ok' && generateResult.data) {
                    set((state) => {
                        state.notifications.push(generateResult.data!);
                        state.unreadNotifications = state.notifications.filter((n) => !n.isRead);
                    });
                } else {
                    set((state) => {
                        state.errors = ['Failed to generate notification'];
                    });
                }
            } catch (e) {
                set((state) => {
                    state.errors = [e.message];
                });
            }
            get().initializeFromRemote();
            set((state) => {
                state.loading = false;
            });
        };

        return {
            // State
            errors: [],
            loading: false,
            notifications: [],
            unreadNotifications: [],

            // Actions
            generateNotification,
            getUserNotificationsAll,
            getUserNotificationsUnread,
            initializeFromRemote,
            markNotificationRead,
            setErrors,
            setLoading,
            setNotifications,
            setUnreadNotifications,
        };
    })
);
